/* eslint-disable react/prop-types */
import { AButton } from 'components/Antd/index';
import React, { useState, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import Select from 'react-select';
import CreatableAsyncSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import { PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useCreatableSelect } from './useCreatableSelect';

const ReactSelect = forwardRef(
  ({ creatable, beforeCreate, createQuery, options, size, mode, isAntdStyle, ...props }, ref) => {
    const [changedAt, setChangedAt] = useState(Date.now());
    const selectRef = useRef();
    const { isLoading, create } = useCreatableSelect(options || []);

    // Determine the component to use based on the props
    const Component = useMemo(() => {
      if (creatable) return mode === 'client' ? CreatableSelect : CreatableAsyncSelect;
      return Select;
    }, [creatable, mode]);

    // Define the styles for the component
    const styleProps = useMemo(
      () => ({
        control:
          size === 'large'
            ? { minHeight: '40px', borderColor: '#d9d9d9' }
            : {
                borderColor: '#d9d9d9',
              },
      }),
      [size]
    );
    const styleProps1 = {
      indicatorsContainer: isAntdStyle
        ? {
            position: 'relative',
            '> *:nth-child(1)': {
              position: 'absolute',
              right: 0,
              background: 'white',
              cursor: 'pointer',
              display: 'none',
              '> svg': {
                background: '#4E5A71',
                borderRadius: '50%',
                width: '17px',
                height: '17px',
                path: {
                  fill: 'white',
                },
              },
            },
          }
        : {},
      container:
        isAntdStyle && !!props.value?.value
          ? {
              '&:hover': {
                'div[class*="indicatorContainer"]': {
                  display: 'flex',
                },
              },
            }
          : {},
    };

    // Define the default props for the component
    const DefaultProps = useMemo(() => {
      if (creatable) {
        return {
          formatCreateLabel: (inputValue) => (
            <AButton type="link" ghost icon={<PlusOutlined />}>
              {inputValue}
            </AButton>
          ),
          onCreateOption: async (inputValue) => {
            const createdOption = await create(inputValue, beforeCreate, createQuery);
            if (props.onChange) {
              props.onChange(props.isMulti ? [...props.value, createdOption] : createdOption);
            }
            // Fixed the Date.now() call
            setChangedAt(Date.now() + 1000);
          },
          isLoading,
          options,
          ...props,
        };
      }
      return {
        ...props,
        options,
      };
    }, [creatable, isLoading, options, props]);

    // Expose the select ref via useImperativeHandle
    useImperativeHandle(ref, () => ({
      select: {
        ...selectRef.current,
      },
    }));

    return (
      <Component
        key={changedAt}
        ref={selectRef}
        styles={{
          container: (baseStyles) => ({
            ...baseStyles,
            ...styleProps1.container,
          }),
          control: (baseStyles) => ({
            ...baseStyles,
            ...styleProps.control,
          }),
          indicatorSeparator: (baseStyles) => ({
            ...baseStyles,
            display: isAntdStyle ? 'none' : undefined,
          }),
          indicatorsContainer: (baseStyles) => ({
            ...baseStyles,
            ...styleProps1.indicatorsContainer,
          }),
        }}
        {...DefaultProps}
      />
    );
  }
);

ReactSelect.propTypes = {
  creatable: PropTypes.bool,
  beforeCreate: PropTypes.func,
  createQuery: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  size: PropTypes.string,
  mode: PropTypes.string,
  onChange: PropTypes.func,
  isMulti: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
    ),
  ]),
};

ReactSelect.defaultProps = {
  creatable: false,
  beforeCreate: () => {},
  createQuery: () => {},
  options: [],
  size: 'medium',
  mode: 'server',
  onChange: () => {},
  isMulti: false,
  value: null,
};

export default ReactSelect;
